/**
 * PORTED (REWRITTEN) — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/accounts-view.tsx`.
 *
 * This is a REWRITE, not a copy. The Next original was a server component that
 * resolved its own tenant and read the vault in its render path:
 *
 *     import "server-only";
 *     const workspaceId = coerceWorkspaceForRead(await resolveTenant());
 *     const selfOrgIds = new Set(listSelfEntityIds("organization", workspaceId));
 *     const orgs = listOrganizations(workspaceId).filter(…);
 *
 * That is `shared-render-ownership.md`'s cause **A** — unportable at any effort,
 * because a browser component cannot open SQLite. All four lines moved behind
 * `crm.views.accounts` (`packages/ui/src/api/contracts/crm/views.ts`, mounted at
 * `excellent/server/routes/crm.ts`), including the self-org exclusion, so the
 * filter cannot drift between the two stacks. The RENDER is verbatim.
 *
 * Changes, and only these:
 *   `import "server-only"`                  -> dropped (this host has no vault)
 *   `async function` + inline vault reads    -> `useApi(crm.views.accounts, {})`
 *   `listOrganizations(...)` rows            -> `data.accounts` (same fields)
 *
 * The contract IS mounted on this host — this component is live, not a stub.
 * `basePath` is accepted for the §5.5 view contract but accounts are
 * workspace-global parties, exactly as in the original.
 */

import {
  DataTable,
  PageHeader,
  EmptyState,
  formatDate,
  ObjectLink,
} from "@excellent/ui";
import { Building2 } from "@excellent/ui/icons";
import { useApi } from "@excellent/ui/api";
import { crm } from "@excellent/ui/api/contracts/crm";

/**
 * Customers ▸ Accounts (Three-Layer PRD §3.6 — capability table line 1172,
 * `Accounts | organizations | org | T1`). Accounts ARE organizations in the sales
 * context — the schema party that CRM leads, opportunities, and synced
 * counterparties tie to. Like Contacts, this is a net-new List over an existing
 * canonical table (`organizations`), not an orphaned product page. Each row opens
 * its object page at `/o/<uid>` (organizations resolve via the `DOMAIN_SPECS`
 * organization fallback). `DataTable` per the table rules; the page owns its
 * `PageHeader`.
 */
export function AccountsView(_props: { basePath: string }) {
  const { data, error, isLoading } = useApi(crm.views.accounts, {});
  const orgs = data?.accounts ?? [];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Accounts"
        description="The companies you sell to — each with its people, deals, and history."
      />
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : orgs.length === 0 ? (
        <EmptyState
          icon={<Building2 className="size-5" />}
          title="No accounts yet"
          description="Organizations appear here as you add companies or sync counterparties from your connected tools."
        />
      ) : (
        <DataTable
          rows={orgs}
          rowKey={(r) => r.id}
          columns={[
            {
              key: "name",
              header: "Name",
              render: (r) => (
                <ObjectLink uid={r.uid} from="customers" className="font-medium hover:underline">
                  {r.name}
                </ObjectLink>
              ),
            },
            {
              key: "website",
              header: "Website",
              render: (r) => r.website ?? "—",
            },
            {
              key: "created",
              header: "Added",
              render: (r) => formatDate(r.created_at),
            },
          ]}
        />
      )}
    </div>
  );
}
